import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useFittingValueCount } from "../hooks/useFittingValueCount";

import { Badge } from "@/components/ui/v2/badge";
import type { ConditionBadgeParts } from "@/features/segments/types";
import { cn } from "@/lib/utils/cn";

const CHIP_VALUE_LIMIT = 3;

type ConditionPartsProps = {
  parts: ConditionBadgeParts;
  truncate?: boolean;
};

export function ConditionParts({
  parts,
  truncate = false,
}: ConditionPartsProps) {
  const { t } = useTranslation();
  const { value } = parts;
  const isChipList = truncate && Array.isArray(value);
  const chipValues = isChipList ? value.slice(0, CHIP_VALUE_LIMIT) : [];
  const { textRef, valuesRef, isMeasuring, fittingCount } = useFittingValueCount(
    chipValues,
    isChipList,
  );
  const shownCount = isMeasuring ? 1 : fittingCount;
  const hiddenCount = isChipList ? value.length - shownCount : 0;

  const renderValue = () => {
    if (isChipList) {
      return chipValues.slice(0, fittingCount).map((chipValue, index) => (
        <Fragment key={chipValue}>
          {index > 0 && ", "}
          <span>{chipValue}</span>
        </Fragment>
      ));
    }
    return Array.isArray(value) ? value.join(", ") : value;
  };

  const text = (
    <>
      {parts.markExcluded && (
        <span className="sr-only">{t("admin.segments.builder.excluded")}</span>
      )}
      <span
        ref={textRef}
        data-slot="condition-text"
        className={cn(
          "min-w-0",
          truncate ? "truncate" : "[overflow-wrap:anywhere]",
          parts.markExcluded && "text-muted-foreground line-through",
        )}
      >
        <span className="font-semibold">{parts.field}</span>
        {parts.operator && (
          <span className="text-muted-foreground"> {parts.operator}</span>
        )}
        {value?.length ? (
          <span className="font-semibold">
            {" "}
            <span ref={valuesRef} data-slot="condition-values">{renderValue()}</span>
          </span>
        ) : null}
      </span>
    </>
  );

  if (!isChipList) return text;

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {text}
      {hiddenCount > 0 && (
        <Badge variant="neutral" size="small" className="shrink-0">
          {t("admin.segments.builder.moreValues", { count: hiddenCount })}
        </Badge>
      )}
    </span>
  );
}
